import { cn } from '@/lib/utils';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import { useOnClickOutside } from '@chiroup/hooks/useOnClickOutside';
import { Listbox, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query';
import { getFocusableElements } from '../a11y/focusable';
import { AlertBlock } from '../AlertBlock';
import EndOfList from '../EndOfList';
import { Loading } from '../Loading';
import { FieldErrors } from './FieldErrors';
import { Input } from './Input';

export type Props = {
  ref?: any;
  name: string;
  className?: string;
  label?: string;
  value?: any;
  limit?: number;
  onChange: (val: any) => void;
  errors?: FormError;
  options?: any[];
  showLabel?: boolean;
  disabled?: boolean;
  alwaysReturnArray?: boolean;
  clearable?: boolean;
  valueProp?: string;
  textProp?: string;
  isFetchingNextPage?: boolean;
  isFetching?: boolean;
  fetchNextPage?: (options?: FetchNextPageOptions | undefined) => Promise<
    InfiniteQueryObserverResult<
      {
        data: any[];
        lastKey?: string;
        skip?: number;
      },
      unknown
    >
  >;
  hasNextPage?: boolean;
  onChangeSearch: (searchTerm: string) => void;
  onEmptyOptions?: () => void;
  searchTerm?: string;
  onAdd?: () => void;
  loading?: boolean;
  serverSide?: boolean;
  searchRef?: React.RefObject<HTMLInputElement | null>;
  buttonRef?: React.RefObject<HTMLInputElement | null>;
  placeholder?: string;
  initialValue?: string;
  initialDisplayValue?: string;
  hideEndOfList?: boolean;
  noneText?: string;
  addOptionTitles?: boolean;
  addTitle?: string;
  add?: () => void;
  autoFocus?: boolean;
  disabledClassName?: string;
  onChangeCallback?: ((obj: any) => void) | null;
  errorMessage?: string;
  errorCloseCallback?: () => void;
  xout?: (() => void) | null;
  // Optional chunked rendering for large option sets
  virtualizeOptions?: boolean;
  virtualChunkSize?: number; // number of options to append per chunk
};

export const Autocomplete = ({
  ref: _ref,
  name,
  className = '',
  label,
  value = '',
  limit,
  onChange,
  errors,
  options = [],
  showLabel = true,
  disabled = false,
  alwaysReturnArray = false,
  clearable = false,
  valueProp = 'value',
  textProp = 'text',
  isFetchingNextPage,
  isFetching,
  fetchNextPage,
  hasNextPage,
  onChangeSearch,
  searchTerm,
  onAdd,
  loading,
  serverSide,
  // eslint-disable-next-line react-hooks/rules-of-hooks
  searchRef = useRef<HTMLInputElement>(null) as any,
  // eslint-disable-next-line react-hooks/rules-of-hooks
  buttonRef = useRef<HTMLInputElement>(null) as any,
  placeholder,
  initialValue,
  initialDisplayValue,
  hideEndOfList = false,
  onEmptyOptions,
  noneText = 'None',
  addOptionTitles = false,
  addTitle,
  add,
  autoFocus,
  disabledClassName = '',
  onChangeCallback = null,
  errorMessage,
  errorCloseCallback,
  xout = null,
  virtualizeOptions = false,
  virtualChunkSize = 100,
}: Props) => {
  const [currentDisplay, setCurrentDisplay] = useState(initialValue === '' ? ' ' : noneText);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsScrollRef = useRef<HTMLDivElement>(null);

  // For multi-select: track pending selections internally, commit on close
  const [pendingSelections, setPendingSelections] = useState<any[]>([]);
  const isMultiSelect = limit !== 1;

  // Sync pending selections with external value ONLY when dropdown opens (not on value changes while open)
  const prevOpenRef = useRef(false);

  useEffect(() => {
    // Only sync when transitioning from closed to open, not on every value change
    if (open && !prevOpenRef.current && isMultiSelect) {
      setPendingSelections(Array.isArray(value) ? value : []);
    }

    prevOpenRef.current = open;
  }, [open, isMultiSelect, value]);

  // Commit pending selections when dropdown closes
  const handleClose = () => {
    if (isMultiSelect && open) {
      // Only call onChange if selections actually changed
      const currentValue = Array.isArray(value) ? value : [];
      const hasChanged =
        pendingSelections.length !== currentValue.length ||
        pendingSelections.some((v, i) => v !== currentValue[i]);

      if (hasChanged) {
        onChange(pendingSelections);
      }
    }

    setOpen(false);
  };

  useOnClickOutside(containerRef as any, handleClose);

  const clear = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();

    // For multi-select (limit > 1), always clear to empty array
    // For single-select, clear to empty string unless alwaysReturnArray is true
    if (isMultiSelect) {
      if (open) {
        setPendingSelections([]);
      } else {
        onChange([]);
      }
    } else {
      onChange(alwaysReturnArray ? [] : '');
    }
  };

  // 'chunked' rendering of options
  const [displayCount, setDisplayCount] = useState<number>(virtualChunkSize);
  const totalOptions = options?.length || 0;
  const displayedOptions = useMemo(() => {
    if (!virtualizeOptions) return options;

    return options.slice(0, Math.min(displayCount, totalOptions));
  }, [options, virtualizeOptions, displayCount, totalOptions]);
  const displayedLength = displayedOptions?.length || 0;

  // reset chunk on options/search changes or when opening the dropdown
  useEffect(() => {
    if (virtualizeOptions) setDisplayCount(virtualChunkSize);
  }, [virtualizeOptions, options, searchTerm, virtualChunkSize]);

  const tryAppendChunk = () => {
    if (!virtualizeOptions) return;

    setDisplayCount(prev => {
      if (prev >= totalOptions) return prev;

      return Math.min(prev + virtualChunkSize, totalOptions);
    });
  };

  const onScrollOptions = (e: React.UIEvent<HTMLDivElement>) => {
    if (!virtualizeOptions) return;

    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 60; // ~60px from bottom

    if (nearBottom) {
      tryAppendChunk();
    }
  };

  const getOptionIndexByValue = (val: string, opts: { withBuffer?: boolean }): number => {
    const additionalBuffer = opts?.withBuffer ? 3 : 0;
    const foundIndex = options.findIndex(option => option.value === val);

    if (foundIndex === -1) return 0;

    // to ensure the selectedoption is in the 'middle' of the shown list,
    // we have to make sure the index is at least 3 items away from the edges of the list
    // AND that we even have that many items to scroll through in the first place
    if (foundIndex <= additionalBuffer || !additionalBuffer) {
      return foundIndex;
    }

    const endOfListIndex = options.length - 1;
    const indexWithBuffer = foundIndex + additionalBuffer;

    if (indexWithBuffer > endOfListIndex) {
      return endOfListIndex;
    }

    return indexWithBuffer;
  };

  // when scrolling through list with arrows keys, ensure the option is visible
  // (list should auto-scroll in the appropriate direction)
  const ensureOptionIsVisible = (index: number) => {
    const container = optionsScrollRef.current as HTMLDivElement | null;

    if (!container) return;

    const optionNodes = Array.from(container.querySelectorAll('[role="option"]')) as HTMLElement[];
    const optionNode = optionNodes[index];

    if (!optionNode) return;

    const top = optionNode.offsetTop;
    const bottom = top + optionNode.offsetHeight;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;

    if (top < viewTop) {
      container.scrollTop = top;
    } else if (bottom > viewBottom) {
      container.scrollTop = bottom - container.clientHeight;
    }
  };

  // Display text management
  useEffect(() => {
    if (limit === 1 && value) {
      const valueDisplay = options?.find(opt => opt[valueProp] === value);
      // For single-select, show the option text, or initialDisplayValue, or keep current display
      // Don't show "1 item selected" which is confusing for single-select

      if (valueDisplay?.[textProp]) {
        setCurrentDisplay(valueDisplay[textProp]);
      } else if (initialDisplayValue) {
        setCurrentDisplay(initialDisplayValue);
      }
      // If neither is available, keep the current display (don't change it)
    } else if (value?.length) {
      const selectedCount = Array.isArray(value)
        ? value.length
        : typeof value === 'string'
          ? value
              .split(',')
              .map(v => v.trim())
              .filter(Boolean).length
          : value != null && value !== ''
            ? 1
            : 0;

      setCurrentDisplay(`${selectedCount} ${selectedCount === 1 ? 'item' : 'items'} selected`);
    } else {
      setCurrentDisplay(initialValue === '' ? ' ' : noneText);
    }

    if (!options?.length && onEmptyOptions) {
      onEmptyOptions();
    }
  }, [
    options,
    value,
    textProp,
    valueProp,
    limit,
    initialDisplayValue,
    initialValue,
    noneText,
    onEmptyOptions,
  ]);

  const onChangeSelections = (val: any) => {
    let hasAdd: boolean;

    if (Array.isArray(val)) {
      hasAdd = val.some((item: string) => item === '$add');
    } else {
      hasAdd = val === '$add';
    }

    if (hasAdd && onAdd) {
      onAdd();
      handleClose();
    } else {
      onChange(val);
    }

    !!onChangeCallback && onChangeCallback(options?.find(opt => opt[valueProp] === val));
  };

  const hasOptions = useMemo(() => {
    return options?.length > 0;
  }, [options]);

  useEffect(() => {
    if (autoFocus && !value) {
      setOpen(true);
    }
  }, [autoFocus, value]);

  useEffect(() => {
    // Reset highlighted index when dropdown closes or options change
    if (!open) {
      setHighlightedIndex(-1);
    }
  }, [open]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [options]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      // When menu open, close and programmatically move to next/previous focus
      if (open) {
        e.preventDefault();
        handleClose();
        setHighlightedIndex(-1);

        // Anchor movement from the component container to follow natural tab order
        const focusables = getFocusableElements();
        const anchor = (containerRef.current as unknown as HTMLElement) || null;
        const outside = focusables.filter(el => (anchor ? !anchor.contains(el) : true));

        // + shift key -> move focus to previous/last element
        if (e.shiftKey) {
          // find last element that precedes the container
          const before = outside.filter(
            el =>
              (anchor && el.compareDocumentPosition(anchor) & Node.DOCUMENT_POSITION_FOLLOWING) !==
              0
          );
          const prev = before.length ? before[before.length - 1] : null;
          prev?.focus();
        } else {
          // just 'tab' -> move focus to next/first element
          const next = outside.find(
            el =>
              (anchor && anchor.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) !==
              0
          );
          next?.focus();
        }

        return;
      }

      // closed -> allow native behavior
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();

      if (!open) {
        setOpen(true);
        setHighlightedIndex(0); // Highlight first option when opening
        // Focus the input field when opening via keyboard
        setTimeout(() => {
          searchRef.current?.focus();
          ensureOptionIsVisible(0);
        }, 0);
      } else {
        const nextIndex = Math.min(highlightedIndex + 1, Math.max(0, displayedLength - 1));

        // If near end of displayed chunk, append next chunk
        if (virtualizeOptions && nextIndex >= displayCount - 5) {
          tryAppendChunk();
        }

        setHighlightedIndex(nextIndex);
        setTimeout(() => ensureOptionIsVisible(nextIndex), 0);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();

      if (open) {
        const prevIndex = Math.max(0, highlightedIndex - 1);
        setHighlightedIndex(prevIndex);
        setTimeout(() => ensureOptionIsVisible(prevIndex), 0);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();

      if (open && highlightedIndex >= 0 && options?.[highlightedIndex]) {
        const selectedOption = options[highlightedIndex];

        if (isMultiSelect) {
          // For multi-select, toggle the selection without closing
          const optionValue = selectedOption[valueProp];
          const isSelected = pendingSelections.includes(optionValue);
          const newSelections = isSelected
            ? pendingSelections.filter((v: any) => v !== optionValue)
            : [...pendingSelections, optionValue];
          setPendingSelections(newSelections);
        } else {
          onChangeSelections(selectedOption[valueProp]);
          setOpen(false);
          // keep focus on the Autocomplete button after selection
          // (so that we don't lose 'tab place' when selecting an option)
          setTimeout(() => {
            buttonRef.current?.querySelector('button')?.focus?.();
          }, 0);
        }
      }

      if (!open) {
        setOpen(true);

        setTimeout(() => {
          ensureOptionIsVisible(getOptionIndexByValue(value, { withBuffer: true }) || 0);
          searchRef.current?.focus();
        }, 0);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();

      // For Escape, discard pending selections and close
      if (isMultiSelect) {
        setPendingSelections(Array.isArray(value) ? value : []);
      }

      setOpen(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <div className={cn(className, 'flex flex-col gap-1')}>
      <AlertBlock level="error" message={errorMessage} closeCallback={errorCloseCallback} />
      <Listbox
        value={isMultiSelect ? pendingSelections : value}
        multiple={isMultiSelect}
        onChange={val => {
          if (!isMultiSelect) {
            onChangeSelections(val);
            setOpen(false);
          } else {
            // For multi-select, update pending selections (don't trigger external onChange yet)
            // Headless UI with multiple={true} passes the complete new array
            setPendingSelections(val);
            // Dropdown stays open, onChange will be called on close
          }
        }}
      >
        <>
          {showLabel && (
            <Listbox.Label
              htmlFor={name}
              className="block text-sm font-medium leading-5 text-gray-900 dark:text-dark-gray-200"
            >
              <div className="inline-block">{label}</div>
            </Listbox.Label>
          )}
          <div className="flex flex-row w-full gap-2" ref={containerRef}>
            {open ? (
              <div className="flex flex-col gap-1 flex-1 relative">
                <Input
                  name="search"
                  className={['w-full dark:text-gray-100', open ? 'block' : 'hidden'].join(' ')}
                  ref={searchRef}
                  onChange={val => onChangeSearch(String(val ?? ''))}
                  value={searchTerm}
                  loading={isFetching || loading}
                  placeholder={placeholder}
                  disabled={disabled}
                  autoFocus={autoFocus}
                  disabledClassName={disabledClassName}
                  xout={xout}
                  onKeyDownCallback={handleKeyDown}
                />
                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className={cn('absolute z-50 mt-10 w-full')}>
                    <div
                      className={cn(
                        'bg-white dark:bg-dark-gray-700 dark:border-dark-gray-600 dark:text-dark-gray-200 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black/5 overflow-auto focus:outline-none sm:text-sm mb-6',
                        hasOptions ? 'block' : 'hidden'
                      )}
                      ref={optionsScrollRef}
                      onScroll={onScrollOptions}
                    >
                      {displayedOptions.map((option, i) => {
                        const isActive = isMultiSelect
                          ? pendingSelections?.includes(option[valueProp])
                          : value === option[valueProp];
                        const isHighlighted = i === highlightedIndex;

                        return (
                          <Listbox.Option
                            title={addOptionTitles ? option[textProp] : undefined}
                            key={option[valueProp] ?? i}
                            className={() =>
                              [
                                isActive
                                  ? 'text-white bg-primary-500 dark:bg-primary-700'
                                  : isHighlighted
                                    ? 'text-gray-900 bg-blue-100 dark:bg-blue-700/30'
                                    : 'text-gray-900',
                                'cursor-default select-none relative py-2 pl-3 pr-9 dark:text-dark-gray-200',
                              ].join(' ')
                            }
                            value={option[valueProp]}
                          >
                            {() => (
                              <>
                                <span
                                  className={[
                                    isActive ? 'font-semibold' : 'font-normal',
                                    'block truncate',
                                  ].join(' ')}
                                >
                                  {option[textProp]}
                                </span>

                                {isActive ? (
                                  <span
                                    className={[
                                      'text-white',
                                      'absolute inset-y-0 right-0 flex items-center pr-4',
                                    ].join(' ')}
                                  >
                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Listbox.Option>
                        );
                      })}
                      {serverSide && !hideEndOfList && (
                        <EndOfList
                          isFetching={!!isFetching}
                          isFetchingNextPage={!!isFetchingNextPage}
                          fetchNextPage={fetchNextPage}
                          hasNextPage={hasNextPage}
                          small
                        />
                      )}
                    </div>
                  </Listbox.Options>
                </Transition>
              </div>
            ) : (
              <div ref={buttonRef} className="flex flex-row gap-2 flex-1 w-full">
                <Listbox.Button
                  onClick={() => {
                    if (disabled) return;

                    setOpen(true);
                    setTimeout(() => {
                      if (value) {
                        ensureOptionIsVisible(
                          getOptionIndexByValue(value, { withBuffer: true }) || 0
                        );
                      }

                      searchRef.current?.focus();
                    }, 0);
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    if (!open && !value) {
                      setOpen(true);
                      setHighlightedIndex(0);
                      setTimeout(() => {
                        searchRef.current?.focus();
                      }, 0);
                    }
                  }}
                  className={cn(
                    'items-center relative file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex min-h-9 w-full min-w-0 rounded-md border px-4 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-white dark:bg-dark-gray-700',
                    'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                    'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
                    errors
                      ? 'border-red-500 text-red-900 placeholder-red-500 focus:border-red-500 focus:ring-red'
                      : 'focus:border-primary-500 focus:ring-primary-500',
                    disabled
                      ? disabledClassName ||
                          'text-gray-400 bg-gray-200 cursor-not-allowed dark:text-dark-gray-300 dark:bg-dark-gray-500'
                      : 'cursor-pointer',
                    'overflow-hidden'
                  )}
                >
                  <div className="flex items-start gap-2 min-w-0 flex-1 pr-7">
                    <span
                      className={cn(
                        'block truncate min-w-0',
                        disabled || currentDisplay === noneText
                          ? disabledClassName || 'text-gray-400'
                          : ''
                      )}
                    >
                      {currentDisplay}
                    </span>
                    {clearable && !disabled && !!(Array.isArray(value) ? value.length : value) && (
                      <XMarkIcon className="h-5 w-5 text-gray-400 shrink-0" onClick={clear} />
                    )}
                  </div>

                  {loading ? (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Loading color="text-gray-400" size={6} />
                    </div>
                  ) : (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  )}
                </Listbox.Button>
              </div>
            )}
            {add && (
              <div className="flex items-center justify-center" title={addTitle}>
                <svg
                  className={[
                    'h-6 w-6 text-gray-300 hover:text-gray-400 cursor-pointer self-center dark:text-dark-gray-600 dark:hover:text-dark-gray-500',
                  ].join(' ')}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  onClick={add}
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </div>
          <FieldErrors errors={errors} />
        </>
      </Listbox>
    </div>
  );
};

export default Autocomplete;
